// utils/twilioService.js
class TwilioService {
  constructor() {
    this.device = null;
    this.call = null;
    this.isInitialized = false;
    this.callbacks = {
      onReady: null,
      onError: null,
      onCallStart: null,
      onCallEnd: null,
      onCallStatusChange: null,
    };
    this.callDuration = 0;
    this.callTimer = null;
  }

  // In twilioService.js - Update initialize method
  async initialize(token) {
    try {
      console.log("Initializing Twilio with token...");
      console.log("Token length:", token.length);
      console.log("Token first 50 chars:", token.substring(0, 50));
      // Clean up if already initialized
      if (this.device) {
        await this.destroy();
      }

      const { Device } = await import("@twilio/voice-sdk");
      console.log("Twilio SDK loaded successfully");

      this.device = new Device(token, {
        edge: ["ashburn", "dublin", "singapore"],
        allowIncomingWhileBusy: true,
        codecPreferences: ["opus", "pcmu"],
        logLevel: "debug", // Change to debug for detailed logs
      });

      // Enhanced event listeners with logging
      this.device.on("registered", () => {
        console.log("✅ Twilio device registered successfully");
        this.updateCallStatus("ready");
      });

      this.device.on("registering", () => {
        console.log("🔄 Twilio device registering...");
      });

      this.device.on("error", error => {
        console.error("❌ Twilio device error:", error);
        console.error("Error code:", error.code);
        console.error("Error message:", error.message);
        this.updateCallStatus("error", error.message);
        this.stopCallTimer();

        if (this.callbacks.onError) {
          this.callbacks.onError(error);
        }
      });

      // Register the device
      console.log("Registering device...");
      await this.device.register();

      this.isInitialized = true;
      console.log("✅ Twilio initialized successfully");

      if (this.callbacks.onReady) {
        this.callbacks.onReady();
      }

      return true;
    } catch (error) {
      console.error("❌ Failed to initialize Twilio:", error);
      console.error("Error stack:", error.stack);

      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }

      throw error;
    }
  }

  setupEventListeners() {
    if (!this.device) return;

    this.device.on("registered", () => {
      console.log("Twilio device registered");
      this.updateCallStatus("ready");
    });

    this.device.on("error", error => {
      console.error("Twilio device error:", error);
      this.updateCallStatus("error", error.message);
      this.stopCallTimer();

      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    });

    this.device.on("incoming", incomingCall => {
      console.log("Incoming call received:", incomingCall);
      // Handle incoming calls if needed
    });
  }

  // Make a call
  async makeCall(phoneNumber, prospectData = {}) {
    if (!this.device || !this.isInitialized) {
      throw new Error(
        "Twilio not initialized. Please call initialize() first."
      );
    }

    if (!phoneNumber) {
      throw new Error("Phone number is required");
    }

    try {
      console.log("Making call to:", phoneNumber);
      this.updateCallStatus("calling");

      // Format phone number
      const formattedNumber = this.formatPhoneNumber(phoneNumber);

      // Make the call
      this.call = await this.device.connect({
        params: {
          To: formattedNumber,
          prospectId: prospectData.id,
          prospectName: `${prospectData.firstName} ${prospectData.lastName}`,
          prospectEmail: prospectData.email,
        },
      });

      console.log("Call object created:", this.call);
      console.log(
        "Call object has 'on' method?",
        typeof this.call.on === "function"
      );

      // Set up call event listeners
      this.setupCallListeners();

      return this.call;
    } catch (error) {
      console.error("Failed to make call:", error);
      this.updateCallStatus("error", error.message);
      throw error;
    }
  }

  setupCallListeners() {
    if (!this.call) return;

    this.call.on("accept", call => {
      console.log("Call accepted:", call);
      this.updateCallStatus("connected");
      this.startCallTimer();

      if (this.callbacks.onCallStart) {
        this.callbacks.onCallStart(call);
      }
    });

    this.call.on("disconnect", call => {
      console.log("Call disconnected:", call);
      this.updateCallStatus("disconnected");
      this.stopCallTimer();
      this.call = null;

      if (this.callbacks.onCallEnd) {
        this.callbacks.onCallEnd(call);
      }
    });

    this.call.on("cancel", () => {
      console.log("Call cancelled");
      this.updateCallStatus("cancelled");
      this.stopCallTimer();
      this.call = null;
    });

    this.call.on("error", error => {
      console.error("Call error:", error);
      this.updateCallStatus("error", error.message);
      this.stopCallTimer();

      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    });
  }

  // Hang up current call
  async hangUp() {
    if (this.call) {
      console.log("Hanging up call...");
      this.call.disconnect();
      this.call = null;
      this.updateCallStatus("disconnected");
      this.stopCallTimer();
    } else if (this.device) {
      this.device.disconnectAll();
    }
  }

  // Format phone number
  formatPhoneNumber(phone) {
    if (!phone) return "";

    // Remove all non-numeric characters except +
    const cleaned = phone.replace(/[^\d+]/g, "");

    // If it doesn't start with +, add country code
    if (!cleaned.startsWith("+")) {
      // Default to US (+1) - change based on your country
      if (cleaned.length === 10) {
        return `+1${cleaned}`; // US numbers
      }
      return `+${cleaned}`; // International numbers
    }

    return cleaned;
  }

  // Start call timer
  startCallTimer() {
    this.stopCallTimer();
    this.callDuration = 0;

    this.callTimer = setInterval(() => {
      this.callDuration++;

      if (this.callbacks.onCallStatusChange) {
        this.callbacks.onCallStatusChange({
          status: "connected",
          duration: this.callDuration,
        });
      }
    }, 1000);
  }

  // Stop call timer
  stopCallTimer() {
    if (this.callTimer) {
      clearInterval(this.callTimer);
      this.callTimer = null;
    }
    this.callDuration = 0;
  }

  // Update call status
  updateCallStatus(status, errorMessage = "") {
    if (this.callbacks.onCallStatusChange) {
      this.callbacks.onCallStatusChange({
        status,
        error: errorMessage,
        duration: this.callDuration,
      });
    }
  }

  // Destroy device
  async destroy() {
    if (this.device) {
      await this.hangUp();
      await this.device.unregister();
      this.device.destroy();
      this.device = null;
      this.isInitialized = false;
      this.stopCallTimer();
    }
  }

  // Register callbacks
  on(event, callback) {
    if (this.callbacks.hasOwnProperty(event)) {
      this.callbacks[event] = callback;
    }
  }

  // Get current call duration
  getCallDuration() {
    return this.callDuration;
  }

  // Check if call is active
  isCallActive() {
    return !!this.call;
  }
}

// Create singleton instance
const twilioService = new TwilioService();
export default twilioService;
